import { datadogRum } from "@datadog/browser-rum";
import { datadogLogs } from "@datadog/browser-logs";
import type { LifecycleSender, RecorderLifecyclePayload } from "@/composables/useNoaRecorderLifecycleTelemetry";

const RUM_CONTEXT_RECORDER_SESSION_ID = "noa.recorder_session_id";
const RUM_CONTEXT_TENANT_LICENSE_ID = "noa.tenant.license_id";
const RUM_CONTEXT_TENANT_PROFESSIONAL_ID = "noa.tenant.professional_id";
const RUM_CONTEXT_TENANT_PMS = "noa.tenant.pms";
const ACTION_PREFIX = "noa_recorder";

// Single source of truth for every NOA event name emitted to Datadog RUM.
// Includes the lifecycle event names mirrored from
// `useNoaRecorderLifecycleTelemetry` (forwarded to BE) plus the modal-local
// events that only land in RUM. The `as const` makes each entry a literal
// string and feeds `NoaRumEventType` so call sites get compile-time typo
// protection.
export const NOA_RUM_EVENT = {
  MODAL_OPENED: "modal_opened",
  MODAL_HIDDEN: "modal_hidden",
  MODAL_CLOSED: "modal_closed",
  IFRAME_LOAD_STARTED: "iframe_load_started",
  IFRAME_LOADED: "iframe_loaded",
  IFRAME_ERROR: "iframe_error",
  IFRAME_DESTROYED: "iframe_destroyed",
  IFRAME_RECREATED: "iframe_recreated",
  RECORDER_READY: "recorder_ready",
  RECORDING_STARTED: "recording_started",
  RECORDING_FINISHED: "recording_finished",
  RECORDING_FINISHED_IFRAME: "recording_finished_iframe",
  DOCUMENT_VISIBILITY_CHANGED: "document_visibility_changed",
  PAGE_LIFECYCLE: "page_lifecycle",
  COMPONENT_UNMOUNTED_DURING_RECORDING: "component_unmounted_during_recording",
  INTERRUPTED_OBSERVED: "interrupted_observed",
  MIC_PERMISSION_DENIED: "mic_permission_denied",
  DEVICE_NOT_FOUND: "device_not_found",
  CLOSE_REQUESTED: "close_requested",
  FINISHED: "finished",
  WS_CONNECTED: "ws_connected",
  WS_DISCONNECTED: "ws_disconnected",
  WS_CONNECT_ERROR: "ws_connect_error",
  WS_ROOM_JOINED: "ws_room_joined",
  SUMMARY_VIEWED: "summary_viewed",
  SUMMARY_EDITED: "summary_edited",
  SUMMARY_ACCEPTED: "summary_accepted",
  SUMMARY_DISCARDED: "summary_discarded",
  UNKNOWN_IFRAME_MESSAGE: "unknown_iframe_message",
} as const;

export type NoaRumEventType = (typeof NOA_RUM_EVENT)[keyof typeof NOA_RUM_EVENT];

// Lifecycle events that also emit a `warn`-level Browser Logs entry. Limited
// to failure-shaped signals so the FE warn channel stays a meaningful alert
// surface rather than a duplicate of the action stream.
const WARN_LEVEL_EVENT_TYPES = new Set<NoaRumEventType>([
  NOA_RUM_EVENT.IFRAME_ERROR,
  NOA_RUM_EVENT.COMPONENT_UNMOUNTED_DURING_RECORDING,
  NOA_RUM_EVENT.INTERRUPTED_OBSERVED,
  NOA_RUM_EVENT.WS_CONNECT_ERROR,
]);

// All four functions below are safe to call before `initObservability` has
// run: the Datadog Browser SDKs are designed to no-op (with an internal
// `console.warn` from the SDK itself) when not initialized, so no try/catch
// guard is needed at the call site.

export function setNoaRumSessionContext(recorderSessionId: string | null | undefined): void {
  if (!recorderSessionId) {
    clearNoaRumSessionContext();
    return;
  }
  datadogRum.setGlobalContextProperty(RUM_CONTEXT_RECORDER_SESSION_ID, recorderSessionId);
}

export function clearNoaRumSessionContext(): void {
  datadogRum.removeGlobalContextProperty(RUM_CONTEXT_RECORDER_SESSION_ID);
}

export type NoaTenantContext = {
  licenseId?: string | null;
  professionalId?: string | null;
  pms?: string | null;
};

/**
 * Attach tenant-level identifiers to every subsequent RUM event so dashboards
 * can split "abandonadas", "slow pipeline" and other known-issue cohorts by
 * clinic + professional + country. Mirrors the snake_case convention used by
 * the recorder session id and the v2-api span tags.
 *
 * Each field is optional and is only set when truthy — missing values clear
 * the corresponding property so a re-bootstrap doesn't leave stale ids
 * attached to a new session.
 */
export function setNoaTenantContext(ctx: NoaTenantContext): void {
  if (ctx.licenseId) {
    datadogRum.setGlobalContextProperty(RUM_CONTEXT_TENANT_LICENSE_ID, String(ctx.licenseId));
  } else {
    datadogRum.removeGlobalContextProperty(RUM_CONTEXT_TENANT_LICENSE_ID);
  }
  if (ctx.professionalId) {
    datadogRum.setGlobalContextProperty(RUM_CONTEXT_TENANT_PROFESSIONAL_ID, String(ctx.professionalId));
  } else {
    datadogRum.removeGlobalContextProperty(RUM_CONTEXT_TENANT_PROFESSIONAL_ID);
  }
  if (ctx.pms) {
    datadogRum.setGlobalContextProperty(RUM_CONTEXT_TENANT_PMS, String(ctx.pms));
  } else {
    datadogRum.removeGlobalContextProperty(RUM_CONTEXT_TENANT_PMS);
  }
}

export function clearNoaTenantContext(): void {
  datadogRum.removeGlobalContextProperty(RUM_CONTEXT_TENANT_LICENSE_ID);
  datadogRum.removeGlobalContextProperty(RUM_CONTEXT_TENANT_PROFESSIONAL_ID);
  datadogRum.removeGlobalContextProperty(RUM_CONTEXT_TENANT_PMS);
}

/**
 * Emit a NOA recorder lifecycle action to Datadog RUM. Failure-shaped events
 * additionally land in Browser Logs at warn level so they are filterable as
 * application warnings independently of RUM session sampling.
 */
export function trackNoaRumAction(eventType: NoaRumEventType, payload?: Record<string, unknown>): void {
  const actionName = `${ACTION_PREFIX}_${eventType}`;
  datadogRum.addAction(actionName, payload);
  if (WARN_LEVEL_EVENT_TYPES.has(eventType)) {
    datadogLogs.logger.warn(actionName, payload);
  }
}

/**
 * Decorate the existing FE→BE LifecycleSender so each payload is also emitted
 * to Datadog RUM as a custom action (and Browser Logs for warn-level types).
 * RUM emission is synchronous and fire-and-forget — the wrapped sender still
 * owns the awaited completion semantics for the BE call.
 */
export function createNoaObservabilityBridgeSender(originalSender: LifecycleSender): LifecycleSender {
  return async (recorderSessionId, payload, delivery) => {
    bridgePayloadToRum(recorderSessionId, payload);
    await originalSender(recorderSessionId, payload, delivery);
  };
}

function bridgePayloadToRum(recorderSessionId: string, payload: RecorderLifecyclePayload): void {
  const { eventType, ...rest } = payload;
  trackNoaRumAction(eventType as NoaRumEventType, {
    recorder_session_id: recorderSessionId,
    ...rest,
  });
}
